// Administración GLOBAL de idiomas (i18n) — EXCLUSIVO del administrador del
// sistema (system_admin).
//
// Dos cosas (Req. multi-idioma):
//   1) Resumen de "cuentas por idioma": cuántas cuentas tienen cada idioma como
//      predeterminado y cuántas lo tienen habilitado.
//   2) Lista de cuentas, donde el admin puede activar/desactivar idiomas y fijar
//      el idioma predeterminado de cualquier cuenta.
//
// Consume `GET /admin/locales` y `PUT /admin/accounts/{tenantId}/locales`.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { AsyncBoundary, Badge, Icon, PageHeader } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import { localeInfo } from '../i18n/locales.js';
import type { Locale, LocaleCount, LocaleInfo, TenantLocaleAccount } from '../types.js';

export function GlobalLocalesView() {
  const { t } = useTranslation();
  const api = useApi();
  const stats = useAsync(() => api.admin.localeStats(), []);
  const [search, setSearch] = useState('');

  return (
    <div className="flex flex-col">
      <PageHeader title={t('adminLocales.title')} subtitle={t('adminLocales.subtitle')} back />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-lg">
        <AsyncBoundary state={stats}>
          {(data) => (
            <>
              <CountsCard title={t('adminLocales.byDefaultTitle')} counts={data.by_default_locale} />
              <CountsCard title={t('adminLocales.byEnabledTitle')} counts={data.by_enabled_locale} />

              <section className="flex flex-col gap-space-sm">
                <div className="flex items-center gap-space-sm">
                  <h2 className="font-headline-sm text-headline-sm font-bold uppercase tracking-widest text-vacuum-glow">
                    {t('adminLocales.accountsTitle')}
                  </h2>
                  <span className="flex-1 h-px bg-gradient-to-r from-vacuum-glow/40 to-transparent" />
                </div>
                <div className="field-slot">
                  <Icon name="search" className="text-on-surface-variant text-[18px] mr-space-xs" />
                  <input
                    className="field-input"
                    placeholder={t('adminLocales.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <AccountList
                  accounts={data.accounts}
                  supported={data.supported}
                  search={search}
                  onSaved={stats.reload}
                />
              </section>
            </>
          )}
        </AsyncBoundary>
      </div>
    </div>
  );
}

/** Tarjeta de conteos por idioma (barra sencilla de "idioma → nº de cuentas"). */
function CountsCard({ title, counts }: { title: string; counts: LocaleCount[] }) {
  const max = Math.max(1, ...counts.map((c) => c.count));
  return (
    <div className="surface-card flex flex-col gap-space-sm">
      <span className="font-headline-sm text-headline-sm text-on-surface">{title}</span>
      <div className="flex flex-col gap-space-xs">
        {counts.map((c) => (
          <div key={c.locale} className="flex items-center gap-space-sm">
            <span className="w-28 shrink-0 font-body-sm text-body-sm text-on-surface truncate" lang={localeInfo(c.locale).tag}>
              {localeInfo(c.locale).nativeName}
            </span>
            <div className="flex-1 h-2 rounded-full bg-surface-container-high overflow-hidden">
              <div
                className="h-full bg-primary-container rounded-full"
                style={{ width: `${(c.count / max) * 100}%` }}
              />
            </div>
            <span className="w-10 text-right font-code-catalog text-body-sm text-on-surface-variant">{c.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccountList({
  accounts,
  supported,
  search,
  onSaved,
}: {
  accounts: TenantLocaleAccount[];
  supported: LocaleInfo[];
  search: string;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === '') return accounts;
    return accounts.filter(
      (a) =>
        (a.tenant_name ?? '').toLowerCase().includes(q) || (a.owner_email ?? '').toLowerCase().includes(q),
    );
  }, [accounts, search]);

  if (filtered.length === 0) {
    return <p className="font-body-sm text-body-sm text-on-surface-variant">{t('adminLocales.noAccounts')}</p>;
  }

  return (
    <div className="flex flex-col gap-space-sm">
      <span className="font-code-catalog text-body-sm text-on-surface-variant">
        {t('adminLocales.accountsCount', { count: filtered.length })}
      </span>
      {filtered.map((a) => (
        <AccountRow key={a.tenant_id} account={a} supported={supported} onSaved={onSaved} />
      ))}
    </div>
  );
}

function AccountRow({
  account,
  supported,
  onSaved,
}: {
  account: TenantLocaleAccount;
  supported: LocaleInfo[];
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [editing, setEditing] = useState(false);
  const [enabled, setEnabled] = useState<Locale[]>(account.enabled_locales);
  const [defaultLocale, setDefaultLocale] = useState<Locale>(account.default_locale);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  function reset() {
    setEnabled(account.enabled_locales);
    setDefaultLocale(account.default_locale);
    setError(null);
    setInfo(null);
  }

  function toggle(code: Locale) {
    setEnabled((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  }

  async function save() {
    // Validación en cliente (el backend revalida de todos modos).
    if (enabled.length === 0) {
      setError(t('adminLocales.atLeastOne'));
      return;
    }
    if (!enabled.includes(defaultLocale)) {
      setError(t('adminLocales.defaultMustBeEnabled'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.admin.setTenantLocales(account.tenant_id, {
        default_locale: defaultLocale,
        enabled_locales: enabled,
      });
      setInfo(t('adminLocales.saved'));
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="surface-card flex flex-col gap-space-sm">
      <div className="flex items-center justify-between gap-space-sm">
        <span className="flex items-center gap-space-xs min-w-0">
          <Icon name="language" className="text-primary text-[18px]" />
          <span className="flex flex-col min-w-0">
            <span className="font-headline-sm text-headline-sm text-on-surface truncate">
              {account.tenant_name ?? account.tenant_id}
            </span>
            {account.owner_email && (
              <span className="font-code-catalog text-body-sm text-on-surface-variant truncate">
                {account.owner_email}
              </span>
            )}
          </span>
        </span>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="shrink-0 inline-flex items-center gap-1 h-9 px-space-sm rounded-lg bg-surface-container-high text-on-surface font-body-sm text-body-sm hover:text-primary transition-colors"
          >
            <Icon name="edit" className="text-[16px]" /> {t('adminLocales.edit')}
          </button>
        )}
      </div>

      {!editing ? (
        <div className="grid grid-cols-1 gap-space-xs font-body-sm text-body-sm">
          <div className="flex items-center gap-space-xs">
            <span className="text-on-surface-variant">{t('adminLocales.defaultLabel')}:</span>
            <Badge tone="info">{localeInfo(account.default_locale).nativeName}</Badge>
          </div>
          <div className="flex items-start gap-space-xs flex-wrap">
            <span className="text-on-surface-variant">{t('adminLocales.enabledLabel')}:</span>
            {account.enabled_locales.map((code) => (
              <Badge key={code} tone="neutral">
                {localeInfo(code).nativeName}
              </Badge>
            ))}
          </div>
          {info && <span className="text-goldmine-mint">{info}</span>}
        </div>
      ) : (
        <div className="flex flex-col gap-space-sm">
          {/* Idiomas habilitados: toggles. */}
          <div className="flex flex-col gap-space-xs">
            <span className="field-label">{t('adminLocales.enabledLabel')}</span>
            <div className="flex flex-wrap gap-space-xs">
              {supported.map((l) => {
                const on = enabled.includes(l.code);
                return (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => toggle(l.code)}
                    aria-pressed={on}
                    lang={l.tag}
                    className={`inline-flex items-center gap-1 h-9 px-space-sm rounded-full border font-body-sm text-body-sm transition-colors ${
                      on
                        ? 'bg-primary-container/15 text-primary border-primary-container/40'
                        : 'bg-surface-container-high text-on-surface-variant border-border-subtle'
                    }`}
                  >
                    <Icon name={on ? 'check_circle' : 'radio_button_unchecked'} className="text-[16px]" />
                    {l.nativeName}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Idioma predeterminado: solo entre los habilitados. */}
          <div className="flex flex-col gap-space-xs">
            <label className="field-label">{t('adminLocales.defaultLabel')}</label>
            <div className="field-slot">
              <Icon name="star" className="text-on-surface-variant text-[18px] mr-space-xs" />
              <select
                className="field-input"
                value={defaultLocale}
                onChange={(e) => setDefaultLocale(e.target.value as Locale)}
              >
                {supported
                  .filter((l) => enabled.includes(l.code))
                  .map((l) => (
                    <option key={l.code} value={l.code} lang={l.tag}>
                      {l.nativeName}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {error && <span className="font-body-sm text-body-sm text-priority-urgent">{error}</span>}

          <div className="flex gap-space-sm">
            <button type="button" className="btn-cyan flex-1" disabled={busy} onClick={() => void save()}>
              <Icon name="save" className="text-[18px]" />
              {busy ? t('adminLocales.saving') : t('adminLocales.save')}
            </button>
            <button
              type="button"
              className="btn-secondary flex-1"
              disabled={busy}
              onClick={() => {
                reset();
                setEditing(false);
              }}
            >
              {t('adminLocales.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
